// src/pages/Reports/EPFESIReport.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Badge,
    Alert,
    Spinner,
    Input,
    Label,
    FormGroup,
    Table,
} from 'reactstrap';
import {
    RefreshCw,
    AlertCircle,
    FileText,
    Download,
    Users,
    TrendingUp,
    CreditCard,
    Briefcase,
} from 'react-feather';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { toast } from 'react-toastify';
import { apiClient } from '../../helpers/api_helper';
import { format } from 'date-fns';
import { fetchUnits, Unit } from 'api/unit';

// ─────────────────────────── types ───────────────────────────
interface Designation {
    id: number;
    title: string;
}

interface EPFESIData {
    employee_code: string;
    employee_name: string;
    designation: string;
    unit: string;

    pf_esi_applicable: boolean;
    pf_number: string | null;
    esi_number: string | null;

    calendar_days: number;
    days_worked: number;

    pf_esi_gross_salary: number;
    earned_basic: number;
    epf_wage: number;
    earned_gross: number;

    pf: number;
    esi: number;
    total_deduction: number;

    epf_employer: number;
    esi_employer: number;

    net_payable: number;
}

interface EPFESISummary {
    total_employees: number;
    total_pf_eligible: number;
    total_esi_eligible: number;
    total_pf_amount: number;
    total_esi_amount: number;
    total_pf_employer_amount: number;
    total_esi_employer_amount: number;
    total_deduction: number;
    total_earned_gross: number;
    total_net_payable: number;
}

interface EPFESIResponseData {
    month: number;
    year: number;
    total_days: number;
    total_working_days: number;
    company_settings: {
        pf_employee_percentage: number;
        pf_employer_percentage: number;
        esi_employee_percentage: number;
        esi_employer_percentage: number;
        epf_wage_ceiling: number;
        esi_gross_ceiling: number;
    };
    employees: EPFESIData[];
    summary: EPFESISummary;
}

interface EPFESIResponse {
    success: boolean;
    data: EPFESIResponseData;
    message?: string;
}

// ─────────────────────────── helpers ───────────────────────────
const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: format(new Date(2000, i, 1), 'MMMM'),
}));

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 11 }, (_, i) => ({
    value: currentYear - 5 + i,
    label: (currentYear - 5 + i).toString(),
}));

const formatCurrency = (amount: number | string | null | undefined): string => {
    const n = typeof amount === 'string' ? parseFloat(amount) : amount;
    return (
        '₹' +
        (n || 0).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })
    );
};

const toNum = (v: any): number => {
    if (v === null || v === undefined) return 0;
    return typeof v === 'string' ? parseFloat(v) || 0 : Number(v);
};

// ─────────────────────────── sub-components ───────────────────────────
interface SummaryCardProps {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({
    title,
    value,
    icon,
    color,
    subtitle,
}) => (
    <Card className="h-100">
        <CardBody>
            <div className="d-flex justify-content-between align-items-start">
                <div>
                    <h6 className="text-muted text-uppercase small mb-2">{title}</h6>
                    <h4 className="mb-0 fw-bold">{value}</h4>
                    {subtitle && <small className="text-muted">{subtitle}</small>}
                </div>
                <div className={`p-3 rounded-circle bg-${color} bg-opacity-10`}>
                    <span className={`text-${color}`}>{icon}</span>
                </div>
            </div>
        </CardBody>
    </Card>
);

// ─────────────────────────── page ───────────────────────────
const EPFESIReport: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [reportData, setReportData] = useState<EPFESIResponseData | null>(null);
    const [error, setError] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const [designations, setDesignations] = useState<Designation[]>([]);
    const [units, setUnits] = useState<Unit[]>([]);
    const [unitsLoading, setUnitsLoading] = useState(false);
    const [filters, setFilters] = useState({
        designation_id: undefined as number | undefined,
        unit_id: undefined as number | undefined,   // ✅ replaces status
    });

    // ── fetch report ──
    const fetchReportData = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const params: any = { year: selectedYear, month: selectedMonth };
            if (filters.designation_id) params.designation_id = filters.designation_id;
            if (filters.unit_id) params.unit_id = filters.unit_id;

            const response: any = await apiClient.get('/epfesi/report', params);

            // apiClient from helpers/api_helper unwraps to `response.data`,
            // so `response` IS the body already.
            const body = response;

            if (body?.success) {
                console.log('EPF/ESI Report:', body.data);
                setReportData(body.data);
            } else {
                const msg = body?.message || 'Failed to fetch EPF/ESI report';
                setError(msg);
                toast.error(msg);
            }
        } catch (err: any) {
            console.error('Error fetching EPF/ESI report:', err);
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to fetch EPF/ESI report';
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, [selectedYear, selectedMonth, filters]);

    const fetchDesignations = useCallback(async () => {
        try {
            const response: any = await apiClient.get('/designations');
            const body = response?.data ?? response;
            if (body) setDesignations(body);
        } catch (err) {
            console.error('Error fetching designations:', err);
        }
    }, []);
    const fetchUnitsData = useCallback(async () => {
        setUnitsLoading(true);
        try {
            const unitsData = await fetchUnits();
            setUnits(unitsData || []);
        } catch (err) {
            console.error('Error fetching units:', err);
        } finally {
            setUnitsLoading(false);
        }
    }, []);
    useEffect(() => {
        fetchDesignations();
        fetchUnitsData();
        fetchReportData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedYear, selectedMonth, filters]);

    const handleFilterChange = (key: string, value: any) =>
        setFilters((prev) => ({ ...prev, [key]: value }));

    const clearFilters = () =>
        setFilters({ designation_id: undefined, unit_id: undefined });

    // ── export excel ──
    const handleExportExcel = async () => {
        if (!reportData || reportData.employees.length === 0) {
            toast.warning('No data to export');
            return;
        }

        try {
            setExporting(true);

            const params = new URLSearchParams({
                year: selectedYear.toString(),
                month: selectedMonth.toString(),
            });
            if (filters.designation_id)
                params.append('designation_id', filters.designation_id.toString());
            if (filters.unit_id !== undefined && filters.unit_id !== null)
                params.append('unit_id', filters.unit_id.toString());

            const token = localStorage.getItem('accessToken');
            const API_URL =
                process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const url = `${API_URL}/epfesi/export?${params.toString()}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept:
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                },
            });

            if (!response.ok) {
                let errorMessage = 'Export failed';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorMessage;
                } catch {
                    const text = await response.text();
                    if (text) {
                        try {
                            errorMessage = JSON.parse(text).message || errorMessage;
                        } catch {
                            errorMessage = `Server error: ${response.status}`;
                        }
                    }
                }
                throw new Error(errorMessage);
            }

            const blob = await response.blob();
            if (blob.size === 0) throw new Error('Received empty file from server');

            const monthName = format(
                new Date(selectedYear, selectedMonth - 1, 1),
                'MMMM_yyyy',
            );
            const filename = `EPF_ESI_Report_${monthName}.xlsx`;

            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(link.href), 100);

            toast.success('EPF/ESI report exported successfully!');
        } catch (err: any) {
            console.error('Export error:', err);
            toast.error(err.message || 'Failed to export EPF/ESI report');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="EPF & ESI Report" pageTitle="Reports" />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div>
                                    <h5 className="card-title mb-0">EPF & ESI Report</h5>
                                    {reportData && (
                                        <small className="text-muted">
                                            {format(
                                                new Date(selectedYear, selectedMonth - 1, 1),
                                                'MMMM yyyy',
                                            )}{' '}
                                            <Badge color="info" className="ms-2">
                                                {reportData.summary.total_employees} Employees
                                            </Badge>
                                            <Badge color="success" className="ms-1">
                                                PF: {reportData.summary.total_pf_eligible}
                                            </Badge>
                                            <Badge color="warning" className="ms-1">
                                                ESI: {reportData.summary.total_esi_eligible}
                                            </Badge>
                                        </small>
                                    )}
                                </div>
                                <div className="d-flex gap-2">
                                    <Button
                                        color="success"
                                        size="sm"
                                        onClick={handleExportExcel}
                                        disabled={
                                            exporting ||
                                            loading ||
                                            !reportData ||
                                            reportData.employees.length === 0
                                        }
                                    >
                                        {exporting ? (
                                            <>
                                                <Spinner size="sm" className="me-1" />
                                                Exporting...
                                            </>
                                        ) : (
                                            <>
                                                <Download size={14} className="me-1" />
                                                Export Excel
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        onClick={fetchReportData}
                                        disabled={loading}
                                    >
                                        <RefreshCw size={14} className="me-1" />
                                        Refresh
                                    </Button>
                                </div>
                            </CardHeader>

                            <CardBody>
                                {/* ── Filters ── */}
                                <div className="mb-3 p-3 border rounded bg-light">
                                    <Row className="g-3 align-items-end">
                                        <Col md={2}>
                                            <FormGroup>
                                                <Label className="form-label-sm">Month</Label>
                                                <Input
                                                    type="select"
                                                    value={selectedMonth}
                                                    onChange={(e) =>
                                                        setSelectedMonth(parseInt(e.target.value))
                                                    }
                                                    bsSize="sm"
                                                >
                                                    {monthOptions.map((m) => (
                                                        <option key={m.value} value={m.value}>
                                                            {m.label}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={2}>
                                            <FormGroup>
                                                <Label className="form-label-sm">Year</Label>
                                                <Input
                                                    type="select"
                                                    value={selectedYear}
                                                    onChange={(e) =>
                                                        setSelectedYear(parseInt(e.target.value))
                                                    }
                                                    bsSize="sm"
                                                >
                                                    {yearOptions.map((y) => (
                                                        <option key={y.value} value={y.value}>
                                                            {y.label}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label className="form-label-sm">Designation</Label>
                                                <Input
                                                    type="select"
                                                    value={filters.designation_id || ''}
                                                    onChange={(e) =>
                                                        handleFilterChange(
                                                            'designation_id',
                                                            e.target.value
                                                                ? parseInt(e.target.value)
                                                                : undefined,
                                                        )
                                                    }
                                                    bsSize="sm"
                                                >
                                                    <option value="">All Designations</option>
                                                    {designations.map((d) => (
                                                        <option key={d.id} value={d.id}>
                                                            {d.title}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label className="form-label-sm">Company</Label>
                                                <Input
                                                    type="select"
                                                    value={filters.unit_id || ''}
                                                    onChange={(e) =>
                                                        handleFilterChange(
                                                            'unit_id',
                                                            e.target.value ? Number(e.target.value) : undefined,
                                                        )
                                                    }
                                                    bsSize="sm"
                                                >
                                                    <option value="">All Companies</option>
                                                    {units.map((unit) => (
                                                        <option key={unit.id} value={unit.id}>
                                                            {unit.name}
                                                            {unit.short_name ? ` (${unit.short_name})` : ''}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={2}>
                                            <FormGroup>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        color="light"
                                                        size="sm"
                                                        onClick={clearFilters}
                                                        className="w-50"
                                                    >
                                                        Clear
                                                    </Button>
                                                    <Button
                                                        color="primary"
                                                        size="sm"
                                                        onClick={fetchReportData}
                                                        className="w-50"
                                                    >
                                                        Apply
                                                    </Button>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </div>

                                {/* ── Company Settings Snapshot ── */}
                                {reportData && (
                                    <div className="mb-3 p-2 border rounded bg-info bg-opacity-10">
                                        <Row className="text-center">
                                            <Col>
                                                <small className="text-muted">PF Employee %</small>
                                                <div className="fw-bold">
                                                    {reportData.company_settings.pf_employee_percentage}%
                                                </div>
                                            </Col>
                                            <Col>
                                                <small className="text-muted">PF Employer %</small>
                                                <div className="fw-bold">
                                                    {reportData.company_settings.pf_employer_percentage}%
                                                </div>
                                            </Col>
                                            <Col>
                                                <small className="text-muted">ESI Employee %</small>
                                                <div className="fw-bold">
                                                    {reportData.company_settings.esi_employee_percentage}%
                                                </div>
                                            </Col>
                                            <Col>
                                                <small className="text-muted">ESI Employer %</small>
                                                <div className="fw-bold">
                                                    {reportData.company_settings.esi_employer_percentage}%
                                                </div>
                                            </Col>
                                            <Col>
                                                <small className="text-muted">EPF Ceiling</small>
                                                <div className="fw-bold">
                                                    {formatCurrency(
                                                        reportData.company_settings.epf_wage_ceiling,
                                                    )}
                                                </div>
                                            </Col>
                                            <Col>
                                                <small className="text-muted">ESI Ceiling</small>
                                                <div className="fw-bold">
                                                    {formatCurrency(
                                                        reportData.company_settings.esi_gross_ceiling,
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                )}

                                {/* ── Error ── */}
                                {error && (
                                    <Alert color="danger" className="mb-3">
                                        <AlertCircle size={16} className="me-2" />
                                        {error}
                                    </Alert>
                                )}

                                {/* ── Loading / Empty / Data ── */}
                                {loading || unitsLoading ? (
                                    <div className="text-center py-5">
                                        <Spinner color="primary" className="me-2" size="lg" />
                                        <span className="ms-2">
                                            {unitsLoading ? 'Loading companies...' : 'Loading EPF/ESI report...'}
                                        </span>
                                    </div>
                                ) : !reportData || reportData.employees.length === 0 ? (
                                    <div className="text-center py-5">
                                        <FileText size={48} className="text-muted mb-3" />
                                        <h5>No records found</h5>
                                        <p className="text-muted">
                                            {format(
                                                new Date(selectedYear, selectedMonth - 1, 1),
                                                'MMMM yyyy',
                                            )}{' '}
                                            has no EPF/ESI data.
                                        </p>
                                        <p className="text-muted small">
                                            Generate payslips for this month first from the{' '}
                                            <strong>Monthly Salary</strong> page.
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        {/* ── Summary Cards ── */}
                                        <Row className="g-3 mb-3">
                                            <Col xl={3} lg={6} md={6}>
                                                <SummaryCard
                                                    title="Total Employees"
                                                    value={reportData.summary.total_employees}
                                                    icon={<Users size={20} />}
                                                    color="primary"
                                                    subtitle={`PF Eligible: ${reportData.summary.total_pf_eligible}  •  ESI Eligible: ${reportData.summary.total_esi_eligible}`}
                                                />
                                            </Col>
                                            <Col xl={3} lg={6} md={6}>
                                                <SummaryCard
                                                    title="Total Earned Gross"
                                                    value={formatCurrency(
                                                        reportData.summary.total_earned_gross,
                                                    )}
                                                    icon={<Briefcase size={20} />}
                                                    color="info"
                                                />
                                            </Col>
                                            <Col xl={3} lg={6} md={6}>
                                                <SummaryCard
                                                    title="Total PF (Emp + Empr)"
                                                    value={formatCurrency(
                                                        reportData.summary.total_pf_amount +
                                                        reportData.summary.total_pf_employer_amount,
                                                    )}
                                                    icon={<TrendingUp size={20} />}
                                                    color="success"
                                                    subtitle={`Employee: ${formatCurrency(
                                                        reportData.summary.total_pf_amount,
                                                    )}  •  Employer: ${formatCurrency(
                                                        reportData.summary.total_pf_employer_amount,
                                                    )}`}
                                                />
                                            </Col>
                                            <Col xl={3} lg={6} md={6}>
                                                <SummaryCard
                                                    title="Total ESI (Emp + Empr)"
                                                    value={formatCurrency(
                                                        reportData.summary.total_esi_amount +
                                                        reportData.summary.total_esi_employer_amount,
                                                    )}
                                                    icon={<TrendingUp size={20} />}
                                                    color="warning"
                                                    subtitle={`Employee: ${formatCurrency(
                                                        reportData.summary.total_esi_amount,
                                                    )}  •  Employer: ${formatCurrency(
                                                        reportData.summary.total_esi_employer_amount,
                                                    )}`}
                                                />
                                            </Col>
                                            <Col xl={6} lg={12}>
                                                <SummaryCard
                                                    title="Total Employee Deduction (PF + ESI)"
                                                    value={formatCurrency(
                                                        reportData.summary.total_deduction,
                                                    )}
                                                    icon={<CreditCard size={20} />}
                                                    color="danger"
                                                />
                                            </Col>
                                            <Col xl={6} lg={12}>
                                                <SummaryCard
                                                    title="Total Net Payable"
                                                    value={formatCurrency(
                                                        reportData.summary.total_net_payable,
                                                    )}
                                                    icon={<CreditCard size={20} />}
                                                    color="success"
                                                />
                                            </Col>
                                        </Row>

                                        {/* ── Main Table ── */}
                                        <div
                                            className="table-responsive"
                                            style={{ maxHeight: '60vh', overflow: 'auto' }}
                                        >
                                            <Table
                                                bordered
                                                hover
                                                striped
                                                size="sm"
                                                className="mb-0 align-middle"
                                                style={{ fontSize: '0.85rem' }}
                                            >
                                                <thead
                                                    className="sticky-top bg-white"
                                                    style={{ zIndex: 10 }}
                                                >
                                                    <tr className="text-center">
                                                        <th style={{ minWidth: '40px' }}>#</th>
                                                        <th style={{ minWidth: '90px' }}>Emp Code</th>
                                                        <th style={{ minWidth: '150px' }}>Employee</th>
                                                        <th style={{ minWidth: '60px' }}>Cal.</th>
                                                        <th style={{ minWidth: '70px' }}>Days</th>
                                                        <th style={{ minWidth: '100px' }}>Earned Basic</th>
                                                        <th style={{ minWidth: '100px' }}>EPF Wage</th>
                                                        <th style={{ minWidth: '110px' }}>Earned Gross</th>
                                                        <th style={{ minWidth: '90px' }}>
                                                            <span className="text-danger">PF (Emp)</span>
                                                        </th>
                                                        <th style={{ minWidth: '90px' }}>
                                                            <span className="text-danger">ESI (Emp)</span>
                                                        </th>
                                                        <th style={{ minWidth: '100px' }}>
                                                            <span className="text-danger">Total Deduct</span>
                                                        </th>
                                                        <th style={{ minWidth: '90px' }}>PF (Empr)</th>
                                                        <th style={{ minWidth: '90px' }}>ESI (Empr)</th>
                                                        <th style={{ minWidth: '100px' }}>Net Payable</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reportData.employees.map((item, index) => (
                                                        <tr key={index}>
                                                            <td className="text-center">{index + 1}</td>
                                                            <td className="text-center">
                                                                {item.employee_code}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex flex-column">
                                                                    <span
                                                                        className="fw-semibold"
                                                                        style={{ fontSize: '0.9rem' }}
                                                                    >
                                                                        {item.employee_name}
                                                                    </span>
                                                                    {item.designation &&
                                                                        item.designation !== '-' && (
                                                                            <small
                                                                                className="text-muted"
                                                                                style={{
                                                                                    fontSize: '10px',
                                                                                    marginTop: '1px',
                                                                                    lineHeight: '1.2',
                                                                                }}
                                                                            >
                                                                                {item.designation}
                                                                                {item.unit && item.unit !== '-' && (
                                                                                    <> • {item.unit}</>
                                                                                )}
                                                                            </small>
                                                                        )}
                                                                </div>
                                                            </td>
                                                            <td className="text-center">
                                                                {item.calendar_days}
                                                            </td>
                                                            <td className="text-center">
                                                                {toNum(item.days_worked).toFixed(1)}
                                                            </td>
                                                            <td className="text-end">
                                                                {formatCurrency(item.earned_basic)}
                                                            </td>
                                                            <td className="text-end">
                                                                {formatCurrency(item.epf_wage)}
                                                            </td>
                                                            <td className="text-end fw-bold">
                                                                {formatCurrency(item.earned_gross)}
                                                            </td>
                                                            <td className="text-end">
                                                                {item.pf > 0 ? (
                                                                    <span className="fw-bold text-danger">
                                                                        {formatCurrency(item.pf)}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">—</span>
                                                                )}
                                                            </td>
                                                            <td className="text-end">
                                                                {item.esi > 0 ? (
                                                                    <span className="fw-bold text-danger">
                                                                        {formatCurrency(item.esi)}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">—</span>
                                                                )}
                                                            </td>
                                                            <td className="text-end fw-bold text-danger">
                                                                {formatCurrency(item.total_deduction)}
                                                            </td>
                                                            <td className="text-end text-muted">
                                                                {formatCurrency(item.epf_employer)}
                                                            </td>
                                                            <td className="text-end text-muted">
                                                                {formatCurrency(item.esi_employer)}
                                                            </td>
                                                            <td className="text-end fw-bold text-success">
                                                                {formatCurrency(item.net_payable)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot className="table-active fw-bold">
                                                    <tr className="text-center">
                                                        <td colSpan={5} className="text-end">
                                                            TOTAL ({reportData.summary.total_employees}{' '}
                                                            employees)
                                                        </td>
                                                        <td />
                                                        <td />
                                                        <td className="text-end">
                                                            {formatCurrency(
                                                                reportData.summary.total_earned_gross,
                                                            )}
                                                        </td>
                                                        <td className="text-end text-danger">
                                                            {formatCurrency(
                                                                reportData.summary.total_pf_amount,
                                                            )}
                                                        </td>
                                                        <td className="text-end text-danger">
                                                            {formatCurrency(
                                                                reportData.summary.total_esi_amount,
                                                            )}
                                                        </td>
                                                        <td className="text-end text-danger">
                                                            {formatCurrency(
                                                                reportData.summary.total_deduction,
                                                            )}
                                                        </td>
                                                        <td className="text-end text-muted">
                                                            {formatCurrency(
                                                                reportData.summary.total_pf_employer_amount,
                                                            )}
                                                        </td>
                                                        <td className="text-end text-muted">
                                                            {formatCurrency(
                                                                reportData.summary.total_esi_employer_amount,
                                                            )}
                                                        </td>
                                                        <td className="text-end text-success">
                                                            {formatCurrency(
                                                                reportData.summary.total_net_payable,
                                                            )}
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </Table>
                                        </div>

                                        {/* ── Footer Notes ── */}
                                        <div className="mt-3 p-2 border rounded bg-light">
                                            <Row className="text-center text-muted small">
                                                <Col md={6}>
                                                    <strong>PF:</strong>{' '}
                                                    {reportData.company_settings.pf_employee_percentage}%
                                                    of Basic (Basic capped at EPF ceiling{' '}
                                                    {formatCurrency(
                                                        reportData.company_settings.epf_wage_ceiling,
                                                    )}
                                                    )
                                                </Col>
                                                <Col md={6}>
                                                    <strong>ESI:</strong>{' '}
                                                    {reportData.company_settings.esi_employee_percentage}
                                                    % of Gross, only if Gross ≤{' '}
                                                    {formatCurrency(
                                                        reportData.company_settings.esi_gross_ceiling,
                                                    )}
                                                </Col>
                                            </Row>
                                            <Row className="mt-1 text-center text-muted small">
                                                <Col>
                                                    Values are sourced from the <strong>payslips</strong>{' '}
                                                    for this month — they match the Monthly Salary page.
                                                </Col>
                                            </Row>
                                        </div>
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default EPFESIReport;